import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { X, Edit2, Check, Download, Share, Copy, Loader, RefreshCw } from 'lucide-react';
import { API } from '../App';

export default function SummaryModal({ plan, token, onClose, color }) {
  const [summary, setSummary] = useState('');
  const [status, setStatus]   = useState('idle'); // idle | updating
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState('');
  const [saving, setSaving]   = useState(false);
  const [copied, setCopied]   = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [sharing, setSharing]   = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState('');
  const [lastSaved, setLastSaved] = useState(null);
  const sseRef = useRef(null);
  const textareaRef = useRef(null);

  // Load summary + subscribe to SSE for live status
  useEffect(() => {
    fetch(`${API}/api/plans/${plan.id}/summary`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { setSummary(d.summary || ''); setStatus(d.status || 'idle'); if (d.updated_at) setLastSaved(d.updated_at); });

    // SSE for live status updates while AI is writing
    const es = new EventSource(`${API}/api/plans/${plan.id}/summary/status-stream?token=${token}`);
    sseRef.current = es;
    es.onmessage = (e) => {
      try {
        const d = JSON.parse(e.data);
        setStatus(d.status);
        if (d.status === 'idle' && d.summary) setSummary(d.summary);
      } catch {}
    };
    return () => es.close();
  }, [plan.id, token]);

  // SSE requires token in query since EventSource doesn't support headers
  // The server uses authMiddleware which reads Bearer — need to add query param auth
  // Actually let's poll instead (simpler, no SSE auth issue)
  useEffect(() => {
    let interval;
    if (status === 'updating') {
      interval = setInterval(async () => {
        const r = await fetch(`${API}/api/plans/${plan.id}/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = await r.json();
        setStatus(d.status);
        if (d.summary) setSummary(d.summary);
        if (d.updated_at) setLastSaved(d.updated_at);
      }, 1500);
    }
    return () => clearInterval(interval);
  }, [status, plan.id, token]);

  // Auto-resize textarea
  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
      textareaRef.current.focus();
    }
  }, [editing, draft]);

  function startEdit() {
    setDraft(summary);
    setEditing(true);
  }

  async function saveSummary() {
    setSaving(true);
    await fetch(`${API}/api/plans/${plan.id}/summary`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ summary: draft }),
    });
    setSummary(draft);
    setLastSaved(Date.now());
    setEditing(false);
    setSaving(false);
  }

  function cancelEdit() {
    setEditing(false);
    setDraft('');
  }

  // PDF via print — inject print styles, trigger window.print
  function handlePrint() {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>${plan.title} — Executive Summary</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 48px; max-width: 800px; margin: 0 auto; color: #111; line-height: 1.7; }
    h1 { font-size: 28px; font-weight: 800; margin-bottom: 4px; }
    .subtitle { font-size: 13px; color: #888; margin-bottom: 36px; border-bottom: 2px solid #eee; padding-bottom: 16px; }
    h2 { font-size: 19px; font-weight: 700; margin: 28px 0 10px; }
    h3 { font-size: 16px; font-weight: 600; margin: 20px 0 8px; }
    p { margin-bottom: 12px; }
    ul, ol { padding-left: 22px; margin-bottom: 12px; }
    li { margin-bottom: 4px; }
    strong { font-weight: 700; }
    em { font-style: italic; }
    code { background: #f4f4f4; padding: 2px 6px; border-radius: 4px; font-size: 0.88em; }
    pre { background: #f4f4f4; padding: 12px 16px; border-radius: 8px; overflow-x: auto; margin-bottom: 14px; }
    blockquote { border-left: 3px solid #ddd; padding-left: 16px; color: #555; margin-bottom: 12px; }
    @media print {
      body { padding: 32px; }
    }
  </style>
</head>
<body>
  <h1>${plan.title}</h1>
  <div class="subtitle">Executive Summary · ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
  <div id="content"></div>
  <script src="https://cdn.jsdelivr.net/npm/marked/marked.min.js"></script>
  <script>
    document.getElementById('content').innerHTML = marked.parse(${JSON.stringify(summary)});
    window.onload = () => { setTimeout(() => { window.print(); window.close(); }, 400); };
  </script>
</body>
</html>`);
    printWindow.document.close();
  }

  async function handleShare() {
    setSharing(true);
    try {
      const r = await fetch(`${API}/api/plans/${plan.id}/summary/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const { token: shareToken } = await r.json();
      const url = `${window.location.origin}/summary/${shareToken}`;
      setShareUrl(url);
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      });
    } catch (err) { console.error(err); }
    setSharing(false);
  }

  async function handleRegenerate() {
    setRegenerating(true);
    setRegenerateError('');
    try {
      const response = await fetch(`${API}/api/plans/${plan.id}/summary/regenerate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Could not start regeneration');
      }
      setStatus('updating');
    } catch (error) { setRegenerateError(error.message); }
    finally { setRegenerating(false); }
  }

  const isEmpty = !summary || summary.trim() === '';

  return (
    <AnimatePresence>
      <motion.div
        key="summary-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.97, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.97 }}
          transition={{ duration: 0.2 }}
          style={{
            width: '100%', maxWidth: 860,
            maxHeight: '92vh',
            background: 'var(--bg-2)',
            border: '1px solid var(--border)',
            borderRadius: 18,
            boxShadow: '0 32px 100px rgba(0,0,0,0.5)',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* ── Header ────────────────────────────────────────────── */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '14px 20px',
            borderBottom: '1px solid var(--border)',
            background: 'var(--bg-3)',
            flexShrink: 0,
          }}>
            {/* Title + status */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {plan.title} — Executive Summary
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 1, display: 'flex', alignItems: 'center', gap: 5 }}>
                {status === 'updating' ? (
                  <>
                    <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.9, ease: 'linear' }}>
                      <Loader size={10} color={color} />
                    </motion.div>
                    <span style={{ color }}>AI is updating summary…</span>
                  </>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span>AI-maintained · edit anytime</span>
                    {lastSaved && (
                      <span style={{ color: 'var(--text-faint)' }}>
                        · Last saved {new Date(Number(lastSaved)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at {new Date(Number(lastSaved)).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </span>
                    )}
                  </span>
                )}
              </div>
              {regenerateError && <div role="alert" style={{ fontSize: 11, color: '#ef4444', marginTop: 3 }}>{regenerateError}</div>}
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              {!editing && (
                <>
                  <ToolBtn
                    onClick={handleRegenerate}
                    title="Regenerate executive summary"
                    icon={regenerating || status === 'updating'
                      ? <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.9, ease: 'linear' }}><RefreshCw size={13} /></motion.div>
                      : <RefreshCw size={13} />}
                    label="Regenerate"
                    disabled={regenerating || status === 'updating'}
                  />
                  <ToolBtn onClick={startEdit} title="Edit summary" icon={<Edit2 size={13} />} label="Edit" color={color} />
                  <ToolBtn onClick={handlePrint} title="Download PDF" icon={<Download size={13} />} label="PDF" />
                  {plan.role === 'owner' && <ToolBtn
                    onClick={handleShare}
                    title={shareUrl ? 'Copy share link' : 'Create share link'}
                    icon={sharing ? <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.9, ease: 'linear' }}><Loader size={13} /></motion.div> : copied ? <Check size={13} color="#10b981" /> : <Share size={13} />}
                    label={copied ? 'Copied!' : 'Share'}
                    active={copied}
                  />}
                </>
              )}
              {editing && (
                <>
                  <ToolBtn
                    onClick={saveSummary}
                    title="Save"
                    icon={saving ? <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.9, ease: 'linear' }}><Loader size={13} /></motion.div> : <Check size={13} />}
                    label="Save"
                    color={color}
                    primary
                  />
                  <ToolBtn onClick={cancelEdit} title="Cancel" icon={<X size={13} />} label="Cancel" />
                </>
              )}
              <button onClick={onClose} style={{
                background: 'var(--bg-4)', border: '1px solid var(--border)',
                borderRadius: 8, width: 32, height: 32, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--text-muted)',
              }}>
                <X size={14} />
              </button>
            </div>
          </div>

          {/* ── Share URL bar ───────────────────────────────────── */}
          {shareUrl && (
            <div style={{
              padding: '8px 20px',
              borderBottom: '1px solid var(--border)',
              background: `${color}12`,
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>Share link:</span>
              <code style={{ flex: 1, fontSize: 11, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shareUrl}</code>
              <button onClick={() => { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color, display: 'flex', padding: 4 }}>
                <Copy size={13} />
              </button>
            </div>
          )}

          {/* ── Body ──────────────────────────────────────────────── */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '28px 36px' }}>
            {editing ? (
              <textarea
                ref={textareaRef}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                style={{
                  width: '100%', minHeight: 400,
                  background: 'var(--bg-4)',
                  border: `1px solid ${color}55`,
                  borderRadius: 10,
                  padding: '16px 18px',
                  color: 'var(--text-primary)',
                  fontSize: 14, lineHeight: 1.75,
                  fontFamily: '"SF Mono", "Fira Code", monospace',
                  resize: 'vertical', outline: 'none',
                }}
                placeholder="Write your executive summary in markdown…"
              />
            ) : isEmpty ? (
              <div style={{ textAlign: 'center', paddingTop: 60 }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>No summary yet</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 340, margin: '0 auto', lineHeight: 1.6 }}>
                  The AI will generate a summary automatically when you create or update cards. You can also write one yourself using the Edit button above.
                </div>
              </div>
            ) : (
              <div className="prose" style={{ fontSize: 15, lineHeight: 1.8, maxWidth: 760 }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{summary}</ReactMarkdown>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function ToolBtn({ onClick, title, icon, label, color, primary, active, disabled }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      title={title}
      style={{
        display: 'flex', alignItems: 'center', gap: 5,
        padding: '5px 10px', borderRadius: 8, cursor: 'pointer',
        fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
        border: `1px solid ${primary ? 'transparent' : 'var(--border)'}`,
        background: primary ? (color || '#6366f1') : active ? `${color || '#6366f1'}18` : 'var(--bg-4)',
        color: primary ? '#fff' : active ? (color || '#6366f1') : 'var(--text-muted)',
        transition: 'all 0.15s',
      }}
    >
      {icon}
      {label}
    </button>
  );
}
